import React, { useEffect, useMemo, useRef, useState } from "react";
import { RCCChartRadarChart, RCCGrid, RCCIcon, RCCPopover, RCCTooltip } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import { DivIcons } from "src/pages/Actions/Panel/components/ListActions/styles";
import formatNumber from "src/utils/formatNumber";
import WrapText from "src/components/WrapText";
import { IMdtbb001, IMdtbb002001, IMdtbb004 } from "../../../interface";
import WrapContext from "../../../styles";
import IDiagnosticAttachmentsRef from "./attachments/interface";
import ModalAttachmentsDiagnostic from "./attachments";

const TabSummary: React.FC<{ data: IMdtbb002001[]; selectedDiagnostic: IMdtbb001 }> = ({
	selectedDiagnostic,
	data
}) => {
	const { translate, currentLanguage } = useTranslation();

	const modalAttachment = useRef<IDiagnosticAttachmentsRef>(null);

	const [listData, setListData] = useState<IMdtbb002001[]>([]);

	const openModalAttachments = (id: number, description: string) => { 
		modalAttachment.current?.openModalComparativeAttachments(id, description);
	};

	const titleListChild = (row: any) => (
		<>
			<WrapContext
				style={{ marginLeft: "20px" }}
			>
				{row.mdb004Descricao}
			</WrapContext>	
		</>
	);

	const listChild = (dt: any) => (
		<List 
			data={dt.mdtbb004}
			columns={[{
				idColumn: "mda002Conceito",
				nameColumn: translate("Conceito"),
				justify: "left",
				xs: 12,
				sm: 10,
				cellRenderer: titleListChild,
				truncate: true
			},{
				idColumn: "totalScore",
				nameColumn: translate("Pontuação obtida"),
				justify: "left",
				xs: 12,
				sm: 1,
				type: "number",
				cellRenderer: (row: any) => formatNumber(row.mdtbb005?.mdb005Pontuacao, currentLanguage) 
			},{
				idColumn: "totalPercentage",
				nameColumn: `% ${translate("Obtido")}`,
				justify: "left",
				xs: 12,
				sm: 1,
				type: "number",
				cellRenderer: (row: any) => (
					<>
						<RCCGrid container item justifyContent="space-between">
							<RCCGrid container item styleInline="width: 30px !important">
								{formatNumber((100 * row.mdtbb005?.mdb005Pontuacao) / dt.mda002Pontuacaomax, currentLanguage)}%
							</RCCGrid>
							<RCCGrid container item styleInline="width: 44px !important">
								{
									row.mdtbb005?.mdb005Complemento.length > 0 ? 
										<>
											<RCCPopover
												header={{title: `${translate("Complemento")}`}}
												body={{
													custom: 
													<WrapText lineClamp={4} title={row.mdtbb005?.mdb005Complemento}>
														{row.mdtbb005?.mdb005Complemento}
													</WrapText>
												}}
												position="leftEnd"
												styleInline="margin-left: 4px;"
											>
												<RCCIcon
													name="commentFilled"
													size={14}
												/>
											</RCCPopover>
										</>

										:

										<>
											<RCCIcon
												name="comment"
												size={14}
											/>
										</>
								}
								<RCCTooltip
									text={translate("Anexos")}
								>
									<DivIcons onClick={() => openModalAttachments(row.mdtbb005?.mdb005Id, row.mdb004Descricao)}>
										<RCCIcon name="attachment" color="var(--default-color-font)" size={12}/>
										{row.mdtbb005?.attachmentsNumber > 0 && row.mdtbb005?.attachmentsNumber}
									</DivIcons>
								</RCCTooltip>
							</RCCGrid>
						</RCCGrid>
					</>
				)
			}]}
			headerHiddenXs={false}
			layout="table"
		/>
	);

	const colConcept = (row: any, index: number | undefined) => (
		<>
			{row.mdtbb004 && row.mdtbb004.length > 0 ? (
				<RCCIcon 
					name={row.toogleButton ? "arrowDown" : "arrowRight"} 
					size={24} 
					style={{ cursor: "pointer" }} 
					onClick={() => {
						setListData((prevState) => {
							const updateState = [...prevState];
							updateState[index ?? 0].toogleButton = 	!prevState[index ?? 0].toogleButton;
							return updateState;
						});
					}}
				/>
			): null}
			{row.bold ? (
				<b>{row.mda002Conceito}</b>
			): (
				<>{row.mda002Conceito}</>
			)}
		</>
	);


	useEffect(() => {

		const sumAllValuesPontuacaomax = (() => {
			let sumValue = 0;
			data?.forEach((element) => {
				sumValue += element.mda002Pontuacaomax;
			});
			return sumValue;
		})();

		const sumAllValuesTotalScore = (() => {
			let sumValue = 0;
			data?.forEach((element) => {
				sumValue += element.totalScore;
			});
			return sumValue;
		})();

		const sumAllValuesTotalPercentage = (sumAllValuesTotalScore * 100) / sumAllValuesPontuacaomax;

		const totalRow: IMdtbb002001 = {
			mda002Titulo: "",
			mda002Conceito: "Total",
			mda002Descricao: "",
			mda002Id: 0,
			mda002Pontuacaomax: sumAllValuesPontuacaomax,
			totalPercentage: sumAllValuesTotalPercentage,
			totalScore: sumAllValuesTotalScore,
			toogleButton: false,
			mdtbb004: [],
			bold: true
		};

		setListData([
			...data.map((mdtbb002: IMdtbb002001) => {
				const mdtbb002Changed: any = mdtbb002;
				mdtbb002Changed.toogleButton = false;
				return mdtbb002Changed;
			}),
			totalRow
		]);

	}, [data]);

	
	const dataComparative = () => { 
		const criteria: any = [];
		data.forEach((mdtbb002: IMdtbb002001) => {
			const criteriaObject: any = [];
			criteriaObject.name = mdtbb002.mda002Titulo;
			criteriaObject.Diagnostic =  mdtbb002.totalPercentage;
			criteria.push(criteriaObject);
			mdtbb002.mdtbb004?.forEach((comparative : any) => {
				criteriaObject[`${comparative.mdb004Id}`] = (100 * comparative?.mdtbb005?.mdb005Pontuacao) / mdtbb002.mda002Pontuacaomax;
			});	
		});
		return criteria;
	};


	const dataChart = dataComparative();

	const criteria = data[0].mdtbb004?.map((mdtba004: IMdtbb004) => ({
		name: mdtba004.mdb004Descricao,
		dataKey: `${mdtba004.mdb004Id.toString()}`
	}));

	const dataRadar = [
		{
			dataKey: "Diagnostic",
			name: selectedDiagnostic.mdb001Descricao
		},
		...criteria	
	];
	
	const RadarChart = useMemo(() => (
		<RCCChartRadarChart 
			width={500}
			height={400}
			data={[...dataChart]}	
			radar={dataRadar}	 		
		 />
	), [data]);

	return (
		<>

			<RCCGrid
				container
				item
				style={{ height: "400px" }}
			>
				{RadarChart}
			</RCCGrid>

			<List 
				data={listData}
				showHeader
				columns={[{
					idColumn: "mda002Conceito",
					nameColumn: translate("Conceito"),
					justify: "left",
					xs: 12,
					sm: 5,
					truncate: true,
					cellRenderer: colConcept
				},{
					idColumn: "mda002Titulo",
					nameColumn: translate("Critério"),
					justify: "left",
					xs: 12,
					sm: 4,
					truncate: true
				},{
					idColumn: "mda002Pontuacaomax",
					nameColumn: translate("Pontuação máxima"),
					justify: "left",
					xs: 12,
					sm: 1,
					type: "number",
					cellRenderer: (row: any) => row.bold ? <b>{formatNumber(row.mda002Pontuacaomax, currentLanguage)}</b> : <>{formatNumber(row.mda002Pontuacaomax, currentLanguage)}</>
				},{
					idColumn: "totalScore",
					nameColumn: translate("Pontuação obtida"),
					justify: "left",
					xs: 12,
					sm: 1,
					type: "number",
					cellRenderer: (row: any) => row.bold ? <b>{formatNumber(row.totalScore, currentLanguage)}</b> : <>{formatNumber(row.totalScore, currentLanguage)}</>
				},{
					idColumn: "totalPercentage",
					nameColumn: `% ${translate("Obtido")}`,
					justify: "left",
					xs: 12,
					sm: 1,
					type: "number",
					cellRenderer: (row: any) => row.bold ? <b>{formatNumber(row.totalPercentage, currentLanguage)}%</b> : <>{formatNumber(row.totalPercentage, currentLanguage)}%</>
				}]}
				childrenOfLine={{
					cellRenderer: listChild,
					conditionToRender: (row: any) => row.toogleButton
				}}
				defaultNumberOfElements={200}
				manuallyToggleShowMore
				headerHiddenXs={false}
				layout="table"
			/>
		
			<ModalAttachmentsDiagnostic 
				ref={modalAttachment}
			/>

		</>
	);
};

export default TabSummary;
